<template>
  <v-text-field
    v-model="date"
    v-maska="dateMask"
    append-inner-icon="mdi-calendar-month-outline"
    :disabled="isDisabled"
    :rules="rules"
    @update:model-value="$emit(
      'date-model',
      date ? dayjs(date, APP_DATE_FORMAT).format(API_DATE_FORMAT) : null,
    )"
  >
    <template #label>
      <span>{{ label }}<sup v-if="isRequired" class="required">*</sup></span>
    </template>
  </v-text-field>
</template>

<script lang="ts" setup>
import type { MaskInputOptions } from 'maska';
import { computed, reactive, ref } from 'vue';
import type { ValidationRule } from 'vuetify';
import {
  API_DATE_FORMAT,
  APP_DATE_FORMAT,
  dayjs,
} from '../../composables/formatter';

interface Props {
  label?: string;
  shouldBeAdult?: boolean;
  maxDate?: dayjs.Dayjs | null;
  minDate?: dayjs.Dayjs | null;
  initValue?: string | null;
  isDisabled?: boolean;
  isRequired?: boolean;
  customRules?: ValidationRule[];
  overrideDefaultRules?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Datum',
  shouldBeAdult: false,
  maxDate: null,
  minDate: null,
  isDisabled: false,
  isRequired: false,
  initValue: null,
  customRules: undefined,
  overrideDefaultRules: false,
});

defineEmits(['date-model']);

const dateMask = reactive<MaskInputOptions>({
  mask: '##.##.####',
  eager: true,
});

const date = ref(props.initValue ? dayjs(props.initValue).format(APP_DATE_FORMAT) : '');

const rules = computed<ValidationRule[]>(() => {
  const result: ValidationRule[] = [
    (val?: string) => !val
      || dayjs(val, APP_DATE_FORMAT, true).isValid()
      || 'Ungültiges Datum',
  ];

  if (props.isRequired) {
    result.push((val?: string) => !!val || 'Dieses Feld wird benötigt!');
  }

  if (props.shouldBeAdult) {
    result.push(
      (val?: string) => !val
        || val.length < dateMask.mask!.length
        || dayjs().diff(dayjs(val, APP_DATE_FORMAT, true), 'years') >= 18
        || 'Der Kunde sollte volljährig sein!',
    );
  }

  if (props.maxDate) {
    result.push(
      (val?: string) => !val
        || val.length < dateMask.mask!.length
        || dayjs(val, APP_DATE_FORMAT, true).isBefore(props.maxDate)
        || `Datum sollte nicht nach ${dayjs(props.maxDate).format(APP_DATE_FORMAT)} liegen`,
    );
  }

  if (props.minDate) {
    result.push(
      (val?: string) => !val
        || val.length < dateMask.mask!.length
        || dayjs(val, APP_DATE_FORMAT, true).isAfter(props.minDate)
        || `Datum sollte nicht vor ${dayjs(props.minDate).format(APP_DATE_FORMAT)} liegen`,
    );
  }

  if (props.customRules) {
    if (props.overrideDefaultRules) {
      return props.customRules;
    }

    result.push(...props.customRules);
  }

  return result;
});
</script>
